import React from 'react';
import { Tag, Tooltip, Space, Typography, Alert } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

/**
 * Shows dynamic approval status for a payment.
 *
 * Props:
 *   status           — payment record status ('PENDING_TREASURY', 'COMPLETED', 'REJECTED')
 *   workflowInstance — ApprovalInstance doc (populated steps.approvers) or null
 */
const ApprovalStatusBadge = ({ status, workflowInstance }) => {
  const navigate = useNavigate();

  if (status === 'COMPLETED') {
    // Distinguish auto-approve from manual approval
    const wasAutoApproved = workflowInstance?.outcome === 'approved' && workflowInstance?.history?.some?.(h => h.auto);
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        {wasAutoApproved ? 'Auto-Approved' : 'Paid'}
      </Tag>
    );
  }

  if (status === 'REJECTED') {
    return <Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag>;
  }

  if (status === 'REVERSED') {
    return <Tag icon={<CloseCircleOutlined />}>Reversed</Tag>;
  }

  if (status !== 'PENDING_TREASURY') {
    return <Tag>{(status || '').replace(/_/g, ' ')}</Tag>;
  }

  // ── PENDING_TREASURY — show who needs to approve ──
  if (!workflowInstance) {
    // Legacy path — no workflow instance
    return (
      <Tag color="orange" icon={<ClockCircleOutlined />}>Pending Approval</Tag>
    );
  }

  const { currentStep, steps = [] } = workflowInstance;
  const activeStep = steps.find(s => s.stepNumber === currentStep) || steps[0];
  const approvers = activeStep?.approvers || [];
  const approverNames = approvers
    .map(a => (typeof a === 'object' ? a.name || a.email : String(a)))
    .filter(Boolean);

  const stepLabel = activeStep?.stepName || `Step ${currentStep}`;

  const tooltipContent = approverNames.length
    ? `${stepLabel}: waiting for ${approverNames.join(', ')}`
    : `${stepLabel}: approver not yet assigned`;

  return (
    <Space direction="vertical" size={2}>
      <Tooltip title={tooltipContent}>
        <Tag
          color="orange"
          icon={<ClockCircleOutlined />}
        >
          Pending Approval
        </Tag>
      </Tooltip>
      {approverNames.length > 0 && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          <UserOutlined style={{ marginRight: 3 }} />
          {approverNames.join(', ')}
        </Text>
      )}
    </Space>
  );
};

/**
 * Alert banner for pending approvals — replaces hardcoded "Awaiting HO Approval".
 * Pass the first pending payment's workflowInstance.
 */
export const PendingApprovalAlert = ({ payments = [] }) => {
  const navigate = useNavigate();
  const pendingPayments = payments.filter(p => p.status === 'PENDING_TREASURY');
  if (!pendingPayments.length) return null;

  // Collect approver info from the first pending payment with a workflow instance
  const firstWithWf = pendingPayments.find(p => p.workflowInstance);
  const wf = firstWithWf?.workflowInstance;

  let approverText = 'waiting for approver assignment';
  let instanceId = null;

  if (wf) {
    instanceId = wf._id;
    const activeStep = (wf.steps || []).find(s => s.stepNumber === wf.currentStep) || wf.steps?.[0];
    const approvers = (activeStep?.approvers || [])
      .map(a => (typeof a === 'object' ? a.name || a.email : String(a)))
      .filter(Boolean);
    if (approvers.length) {
      approverText = `waiting for: ${approvers.join(', ')}`;
    } else if (activeStep?.stepName) {
      approverText = `step: ${activeStep.stepName}`;
    }
  }

  const count = pendingPayments.length;
  const description = (
    <span>
      {count} payment{count > 1 ? 's' : ''} pending approval — {approverText}.
      {' '}The case balance will update once approved.
      {/* {instanceId && (
        <span
          style={{ marginLeft: 8, color: '#1677ff', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate(`/approvals/instance/${instanceId}`)}
        >
          View approval →
        </span>
      )} */}
    </span>
  );

  return (
    <Alert
      type="warning"
      showIcon
      icon={<ClockCircleOutlined />}
      message="Pending Approval"
      description={description}
      style={{ marginBottom: 12 }}
    />
  );
};

export default ApprovalStatusBadge;
